import { cloneMeasureEntityRef, type MeasureEntityRef } from '../types/measure-types';
import {
    MeasurePairReferenceMode,
    type MeasureReferencePolicy,
} from '../types/measure-reference-policy-types';

/**
 * 测量拾取后的选中集变更策略。
 *
 * 职责：
 * 1. 按参考点策略计算下一次选中集；
 * 2. 点到点模式下已满双选时执行「先选保留、后选滑动替换」；
 * 3. 裁剪已移出选中集的拾取历史，避免活动锚点误关联。
 */
export class MeasurePickSelectionPolicy {
    /**
     * 根据当前选中集与参考点策略，计算下一次选中集。
     *
     * 规则：
     * 1. 重复点击已选实体 → toggle 移除（全模式通用）；
     * 2. 点到点模式且已选满 2 个 → 保留先选、用新实体替换后选；
     * 3. 其余情况 → 追加新实体。
     *
     * @param current 当前选中集。
     * @param incoming 本次拾取实体。
     * @param policy 双选参考点策略。
     * @returns 下一帧选中集副本。
     */
    public resolveNextSelection(
        current: readonly MeasureEntityRef[],
        incoming: MeasureEntityRef,
        policy: MeasureReferencePolicy,
    ): MeasureEntityRef[] {
        const existingIndex = current.findIndex(
            (item) => item.objectId === incoming.objectId,
        );

        // 重复点击：toggle 取消选中，行为与全模式一致。
        if (existingIndex >= 0) {
            return current
                .filter((_, index) => index !== existingIndex)
                .map((entity) => cloneMeasureEntityRef(entity));
        }

        // 点到点双选已满：滑动替换后选，先选锚定不变。
        if (
            policy.pairMode === MeasurePairReferenceMode.PointToPoint
            && current.length >= 2
        ) {
            const firstEntity = current[0];
            if (!firstEntity) {
                return [cloneMeasureEntityRef(incoming)];
            }
            return [
                cloneMeasureEntityRef(firstEntity),
                cloneMeasureEntityRef(incoming),
            ];
        }

        // 默认：追加到选中集末尾。
        return [
            ...current.map((entity) => cloneMeasureEntityRef(entity)),
            cloneMeasureEntityRef(incoming),
        ];
    }

    /**
     * 裁剪已移出选中集的拾取历史条目。
     *
     * @param pickHistory 当前拾取历史。
     * @param selectedEntities 当前选中集。
     * @returns 裁剪后的拾取历史副本。
     */
    public prunePickHistory(
        pickHistory: readonly MeasureEntityRef[],
        selectedEntities: readonly MeasureEntityRef[],
    ): MeasureEntityRef[] {
        const selectedIdSet = new Set(
            selectedEntities.map((entity) => entity.objectId),
        );
        return pickHistory
            .filter((entry) => selectedIdSet.has(entry.objectId))
            .map((entry) => cloneMeasureEntityRef(entry));
    }
}
